class AlgoliaFilterButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));

    // Wait for Algolia to initialize
    const checkAlgolia = setInterval(() => {
      if (window.algoliaShopify && window.algoliaShopify.instantsearch && window.algoliaShopify.instantsearch.search) {
        clearInterval(checkAlgolia);
        this.initializeState();
        this.setupStateChangeListener();
      }
    }, 100);
  }

  setupStateChangeListener() {
    const helper = window.algoliaShopify.instantsearch.search.helper;
    this.stateChangeListener = ({ state }) => {
      // Get all active filters
      const activeFilters = {
        disjunctiveFacets: state.disjunctiveFacetsRefinements || {},
        otherRefinements: state.facetsRefinements || {},
      };

      // Get all filter buttons
      const filterButtons = document.querySelectorAll('algolia-filter-button');

      // Reset all buttons first
      filterButtons.forEach((button) => {
        button.classList.remove('is-active');
      });

      // Check each button against active filters
      filterButtons.forEach((button) => {
        const buttonValue = button.dataset.value;

        // Parse the URL-style parameter into Algolia format (same as initializeState)
        const params = new URLSearchParams(buttonValue);
        const refinements = {};

        params.forEach((value, key) => {
          const match = key.match(/refinementList\[(.*?)\]/);
          if (match) {
            const attribute = match[1];
            if (!refinements[attribute]) {
              refinements[attribute] = [];
            }
            refinements[attribute].push(value);
          }
        });

        // Check if any of this button's refinements are active (same logic as initializeState)
        const isActive = Object.entries(refinements).some(([attribute, values]) => {
          return values.some((value) => {
            const hasExactMatch =
              activeFilters.disjunctiveFacets[attribute] && activeFilters.disjunctiveFacets[attribute].includes(value);

            // Check if removing the last word matches any existing refinements
            const words = value.split(' ');
            const partialMatch = words.length > 1 ? words.slice(0, -1).join(' ') : null;
            const hasPartialMatch =
              partialMatch &&
              activeFilters.disjunctiveFacets[attribute] &&
              activeFilters.disjunctiveFacets[attribute].includes(partialMatch);

            return hasExactMatch || hasPartialMatch;
          });
        });

        if (isActive) {
          button.classList.add('is-active');
        }
      });

      this.dispatchEvent(
        new CustomEvent('algolia-state-change', {
          detail: { state },
          bubbles: true,
        }),
      );
    };
    helper.on('change', this.stateChangeListener);
  }

  disconnectedCallback() {
    // Clean up the listener when the element is removed
    if (
      this.stateChangeListener &&
      window.algoliaShopify &&
      window.algoliaShopify.instantsearch &&
      window.algoliaShopify.instantsearch.search
    ) {
      const helper = window.algoliaShopify.instantsearch.search.helper;
      helper.removeListener('change', this.stateChangeListener);
    }
  }

  initializeState() {
    const helper = window.algoliaShopify.instantsearch.search.helper;

    const { value } = this.dataset;

    // Parse the URL-style parameter into Algolia format
    const params = new URLSearchParams(value);
    const refinements = {};

    params.forEach((value, key) => {
      const match = key.match(/refinementList\[(.*?)\]/);
      if (match) {
        const attribute = match[1];
        refinements[attribute] = [value];
      }
    });

    // Check if any of our refinements are active
    Object.entries(refinements).forEach(([attribute, values]) => {
      const isActive = values.some((value) => {
        const hasExactMatch =
          helper.state.disjunctiveFacetsRefinements &&
          helper.state.disjunctiveFacetsRefinements[attribute] &&
          helper.state.disjunctiveFacetsRefinements[attribute].includes(value);

        // Check if removing the last word matches any existing refinements
        const words = value.split(' ');
        const partialMatch = words.length > 1 ? words.slice(0, -1).join(' ') : null;
        const hasPartialMatch =
          partialMatch &&
          helper.state.disjunctiveFacetsRefinements &&
          helper.state.disjunctiveFacetsRefinements[attribute] &&
          helper.state.disjunctiveFacetsRefinements[attribute].includes(partialMatch);

        return hasExactMatch || hasPartialMatch;
      });

      if (isActive) {
        this.classList.add('is-active');
      }
    });
  }

  clearActiveFilters() {
    let allActiveFilters = document.querySelectorAll('.algolia-filter-button.is-active');
    allActiveFilters.forEach((filter) => {
      filter.classList.remove('is-active');
    });
  }

  handleClick() {
    const helper = window.algoliaShopify.instantsearch.search.helper;
    this.clearActiveFilters();

    const { value } = this.dataset;

    try {
      // Parse the URL-style parameter into Algolia format
      const params = new URLSearchParams(value);
      const refinements = {};

      // Collect all refinements, handling array-style parameters
      params.forEach((value, key) => {
        // Match both refinementList[attribute] and refinementList[attribute][0]
        const match = key.match(/refinementList\[(.*?)\](?:\[\d+\])?$/);
        if (match) {
          const attribute = match[1];
          if (!refinements[attribute]) {
            refinements[attribute] = [];
          }
          refinements[attribute].push(value);
        }
      });

      // Check if any of the refinements are active
      const hasActiveRefinement = Object.entries(refinements).some(([attribute, values]) =>
        values.some(
          (value) =>
            helper.state.disjunctiveFacetsRefinements &&
            helper.state.disjunctiveFacetsRefinements[attribute] &&
            helper.state.disjunctiveFacetsRefinements[attribute].includes(value),
        ),
      );

      if (hasActiveRefinement) {
        // When turning off, remove all refinements from this button
        Object.entries(refinements).forEach(([attribute, values]) => {
          values.forEach((value) => helper.removeDisjunctiveFacetRefinement(attribute, value));
        });
        this.classList.remove('is-active');
        // Remove params from URL
        const newUrl = new URL(window.location.href);
        params.forEach((_, key) => {
          if (key.startsWith('refinementList')) {
            newUrl.searchParams.delete(key);
          }
        });
        window.history.pushState({}, '', newUrl);
      } else {
        // When turning on, clear all existing refinements first
        helper.state.disjunctiveFacets.forEach((facet) => {
          helper.clearRefinements(facet);
        });

        // Then add all our new refinements
        Object.entries(refinements).forEach(([attribute, values]) => {
          if (!helper.state.disjunctiveFacets.includes(attribute)) {
            helper.state.disjunctiveFacets.push(attribute);
          }
          values.forEach((value) => helper.addDisjunctiveFacetRefinement(attribute, value));
        });

        // Replace URL params with our new ones
        const newUrl = new URL(window.location.href);
        newUrl.search = value;
        window.history.pushState({}, '', newUrl);
      }

      // Open filters if they're not already open
      const filterButton = document.querySelector('.ais-toggle-filters');
      if (filterButton && !document.querySelector('.ais-facets').classList.contains('ais-facets__shown')) {
        filterButton.click();
      }

      // Trigger a new search
      helper.search();
    } catch (error) {
      console.error('Error during refinement toggle:', error);
    }
  }
}

customElements.define('algolia-filter-button', AlgoliaFilterButton);
//
